<template>
  <section>
    <header class="d-flex align-items-center">
      <AppHeading class="mb-0 flex-1">
        <span data-whatfix-id="settings-terms-heading">Terms</span>
      </AppHeading>
      <AppButton
        v-if="editable"
        raised
        @click="isAddTermModalOpen = true"
      >
        <AppIcon name="add" />
        Add Term
      </AppButton>
      <AppBadge v-else>Read only</AppBadge>
    </header>
    <div class="rounded p-2 mt-4 bg-light mb-4">
      <slot name="config">
        <div class="row">
          <!-- These have to have a type check, as default value is Symbol,
          which causes type error because of Vue's prop check. -->
          <TermSelect
            label="Current Active Term"
            :value="typeof currentTerm === 'object' ? currentTerm : {}"
            :terms="terms"
            :disabled="!editable"
            class="col col-xl-4 pl-0 pt-0"
            @input="$store.dispatch('setCurrentTerm', { ...$event, forceReload: true })"
          />
          <TermSelect
            label="Current Scheduling Term"
            :value="typeof currentPlanningTerm === 'object' ? currentPlanningTerm : {}"
            :terms="terms"
            :disabled="!editable"
            class="col col-xl-4 pr-0 pt-0"
            data-test="currentSchedulingTermSelect"
            @input="$store.dispatch('setCurrentPlanningTerm', { ...$event, forceReload: true })"
          />
        </div>
        <SemesterNames
          :semesterNames="typeof semesterNames === 'object' ? semesterNames : {}"
          class="mt-4"
          :editable="editable"
          @input="$store.dispatch('setSemesterNames', $event)"
        />
      </slot>
    </div>
    <div class="mb-3 d-flex justify-content-end">
      <FormCheckbox
        :value="showHistoricalTerms"
        class="d-block"
        label="Show Historical Terms"
        @input="setShowHistoricalTerms($event)"
      />
    </div>
    <AppTable
      :rows="showHistoricalTerms ? termsWithHistorical : terms"
      class="mt-4"
      resourceName="Terms"
      @select="selectTerm"
    >
      <template #header>
        <th class="th">Display Name</th>
        <th class="th">Start Date</th>
        <th class="th">End Date</th>
        <th v-if="shouldDisplayPhase" class="th">{{ phaseLabel }}</th>
        <th class="th">
          <span data-whatfix-id="settings-terms-th-historical">Historical</span>
        </th>
      </template>
      <template #default="{ row: term }">
        <td
          :class="{ 'text-muted': !term.displayName }"
          data-test="termDisplayName"
        >{{ term.displayName || 'Not set' }}</td>
        <td data-test="termStartDate">{{ formatISODateToLocale(term.startDate) }}</td>
        <td data-test="termEndDate">{{ formatISODateToLocale(term.endDate) }}</td>
        <td v-if="shouldDisplayPhase">{{ getPhaseName(term.phaseId) }}</td>
        <td>
          <AppBadge
            v-if="term.historical"
            variant="gray"
            pill
          >
            <span class="text-dark">Historical</span>
          </AppBadge>
        </td>
      </template>
    </AppTable>
    <AddTermModal
      v-if="isAddTermModalOpen"
      @close="isAddTermModalOpen = false"
    />
  </section>
</template>

<script>
import { mapState, mapGetters, mapMutations } from 'vuex'
import TermSelect from '@sm/client/components/Terms/TermSelect'
import SemesterNames from '@sm/client/components/Terms/SemesterNames'
import AddTermModal from '@sm/client/components/Terms/AddTermModal'
import FormCheckbox from '@/components/Forms/FormCheckbox'
import useTermTemplate from '@sm/client/components/Terms/composables/useTermTemplate'
import useFeatureFlags from '@/components/composables/useFeatureFlags'
import { SM_SHOW_PHASES_IN_TERMS_LIST } from '@shared/constants/featureFlags'

export default {
  components: {
    TermSelect,
    SemesterNames,
    AddTermModal,
    FormCheckbox,
  },
  props: {
    editable: {
      type: Boolean,
      default: false,
    },
    product: {
      type: String,
      default: '',
    },
  },
  data: () => ({
    isAddTermModalOpen: false,
    termTemplate: {},
  }),

  setup () {
    const { showPhasesInTermsList } = useFeatureFlags({
      showPhasesInTermsList: SM_SHOW_PHASES_IN_TERMS_LIST,
    })

    return {
      showPhasesInTermsList,
    }
  },

  computed: {
    ...mapState({
      currentTerm: state => state.general.terms.currentTerm,
      currentPlanningTerm: state => state.general.terms.currentPlanningTerm,
      showHistoricalTerms: state => state.general.terms.showHistoricalTerms,
    }),
    ...mapGetters([
      'semesterNames',
      'terms',
      'termsWithHistorical',
      'school',
    ]),
    ...mapGetters({
      phases: 'phases/getAll',
    }),
    shouldDisplayPhase () {
      const enabledProducts = [ 'sm' ]

      return this.showPhasesInTermsList && enabledProducts.includes(this.product)
    },
    phaseTemplate () {
      return this.termTemplate.questions?.phaseId || {}
    },
    phaseLabel () {
      return this.phaseTemplate.label || 'Phase'
    },
  },
  async created () {
    const { termTemplate, loadTermTemplate } = useTermTemplate()
    await loadTermTemplate({ useCache: true })

    this.termTemplate = termTemplate.value
  },
  methods: {
    ...mapMutations({ setShowHistoricalTerms: 'SET_SHOW_HISTORICAL_TERMS' }),
    selectTerm (term) {
      this.$emit('selectTerm', term)
    },
    getPhaseName (phaseId) {
      if (!phaseId) return 'No phase'
      return this.phases.find(({ id }) => phaseId === id)?.name || 'Phase not found'
    },
  },
}
</script>
